import type { LocationQuery, RouteRecordNameGeneric } from 'vue-router'
import type { AnalystSurface, Breakdown, Fact, PipelineStep, ScreenContext } from '@/api/analyst'
import type { PeriodSelection } from '@/api/snapshot'
import type { OperationsMetricId } from '@/api/snapshot'
import type { ContinuityMetricId } from '@/types/intelligence'
import { METRIC_KEYS, formatDayRange } from './metric-format'
import { CONTINUITY_KEYS } from './continuity-format'
import { isCompletePeriod, periodFromQuery } from './period-query'

type Translate = (key: string, values?: Record<string, unknown>) => string
/** Whether the reader's language has a string for a key (vue-i18n's `te`). */
type Exists = (key: string) => boolean

/** The surface each analysis page asks from, and the period it asks about when the page names none in its address. */
const SURFACES: Record<string, { surface: AnalystSurface; fallback: Exclude<PeriodSelection['period'], 'CUSTOM'>; fromAddress: boolean }> = {
  'workspace-overview': { surface: 'OVERVIEW', fallback: 'LAST_7_DAYS', fromAddress: true },
  'workspace-opportunities': { surface: 'OPPORTUNITIES', fallback: 'LAST_30_DAYS', fromAddress: true },
  'workspace-opportunity': { surface: 'OPPORTUNITY', fallback: 'LAST_30_DAYS', fromAddress: false },
  // Objectives are measured over the last 30 days (P5 §2.1).
  'workspace-objectives': { surface: 'OBJECTIVES', fallback: 'LAST_30_DAYS', fromAddress: false },
  'workspace-reports': { surface: 'REPORTS', fallback: 'LAST_7_DAYS', fromAddress: false },
  'workspace-report': { surface: 'REPORT', fallback: 'LAST_7_DAYS', fromAddress: false },
  'workspace-attention': { surface: 'ATTENTION', fallback: 'LAST_7_DAYS', fromAddress: false },
}

/**
 * The screen a question is asked from, and what it shows: the question
 * inherits it, so "why did it rise?" needs no restating (P4 §3). Every
 * analysis page asks; settings do not (P4 v1.5). Null where Ask Elfie is not
 * offered.
 */
export const screenContextOf = (route: { name: RouteRecordNameGeneric; query: LocationQuery }): ScreenContext | null => {
  const page = typeof route.name === 'string' ? SURFACES[route.name] : undefined
  if (!page) return null
  if (!page.fromAddress) return { surface: page.surface, period: { period: page.fallback } }
  const period = periodFromQuery(route.query, page.fallback)
  // A chosen range still missing a day shows the numbers asked for before it: the page's default horizon.
  return { surface: page.surface, period: isCompletePeriod(period) ? period : { period: page.fallback } }
}

/** Questions that get an answer from the data today (P4 §2, v1.5). Keys under `analyst.suggestions`; the same on every page until a page's own data can answer more. */
const TODAY = [
  'analyst.suggestions.overview.waitingTime',
  'analyst.suggestions.overview.comparePrevious',
  'analyst.suggestions.overview.patientsSeen',
  // The care journey, which Elfie answers from too (DECISIONS.md #39).
  'analyst.suggestions.overview.chronicCare',
]
export const SUGGESTIONS: Record<AnalystSurface, string[]> = {
  OVERVIEW: TODAY,
  OPPORTUNITIES: TODAY,
  OPPORTUNITY: TODAY,
  OBJECTIVES: TODAY,
  REPORTS: TODAY,
  REPORT: TODAY,
  ATTENTION: TODAY,
}

/**
 * The line a reader follows while the engine works: its nine steps (S1 §2)
 * in four stages. Packaging and the audit record are not the reader's
 * concern, so the line stays on the check until the answer arrives.
 */
export type Stage = 'READING' | 'COMPUTING' | 'WRITING' | 'CHECKING'

const STAGE_OF: Record<PipelineStep, Stage> = {
  INTENT: 'READING',
  PLAN: 'READING',
  METRICS: 'COMPUTING',
  DATA: 'COMPUTING',
  EXECUTE: 'COMPUTING',
  INTERPRET: 'WRITING',
  VERIFY: 'CHECKING',
  PACKAGE: 'CHECKING',
  AUDIT: 'CHECKING',
}

export const stageOf = (step: PipelineStep): Stage => STAGE_OF[step]

/** A metric's name in the reader's language; its registry id where the screen has no name for it yet. */
export const metricName = (metricId: string, t: Translate): string => {
  const key = METRIC_KEYS[metricId as OperationsMetricId]
  if (key) return t(`overview.metrics.${key}.name`)
  // A Care Quality & Continuity measure (DECISIONS.md #39).
  const care = CONTINUITY_KEYS[metricId as ContinuityMetricId]
  return care ? t(`overview.continuity.measures.${care}.name`) : metricId
}

/** A list in the reader's language: "Median waiting time and Visits completed". Names keep their case: German nouns need theirs. */
export const listOf = (items: string[], locale: string): string =>
  new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items)

/** What the stage line says, with the metrics being computed when the engine names them. */
export const stageText = (
  turn: { stage: Stage | null; attempt: number; metricIds: string[]; period: { first: string; last: string } | null },
  t: Translate,
  locale: string,
): string => {
  if (turn.stage === null) return ''
  if (turn.stage === 'WRITING' && turn.attempt > 1) return t('analyst.stages.REWRITING')
  if (turn.stage !== 'COMPUTING') return t(`analyst.stages.${turn.stage}`)
  if (turn.metricIds.length === 0) return t('analyst.stages.COMPUTING_ANY')
  const metrics = listOf(turn.metricIds.map((id) => metricName(id, t)), locale)
  return turn.period
    ? t('analyst.stages.COMPUTING_PERIOD', { metrics, range: formatDayRange(turn.period.first, turn.period.last, locale) })
    : t('analyst.stages.COMPUTING', { metrics })
}

/**
 * Error codes the panel has its own words for. Anything else reads as a
 * plain failure in the reader's language: Intelligence screens translate
 * codes and never show the server's English message (S3–S5 rulings).
 */
const KNOWN_ERRORS = new Set(['ANALYST_DISABLED', 'LLM_UNAVAILABLE', 'LLM_INVALID_OUTPUT', 'NETWORK_ERROR', 'STREAM_ENDED'])

export const errorText = (error: { code: string }, t: Translate): string =>
  KNOWN_ERRORS.has(error.code) ? t(`analyst.errors.${error.code}`) : t('common.somethingWentWrong')

/**
 * A fact's value as the reader expects it: counts with their separators,
 * minutes to one decimal (the precision the engine checks), changes signed,
 * a relative change and a care-journey share as a percentage, and a share's
 * move in percentage points.
 */
export const factValue = (fact: Fact, locale: string, t: Translate): string => {
  if (fact.unit === 'COEFFICIENT') {
    const r = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(fact.value)
    return t('analyst.statistic', { value: r })
  }
  const signed = fact.window === 'CHANGE'
  if (fact.unit === 'PERCENTAGE_POINTS') {
    const points = new Intl.NumberFormat(locale, { maximumFractionDigits: 1, signDisplay: 'exceptZero' }).format(fact.value)
    return t('overview.continuity.points', { value: points })
  }
  if (fact.unit === 'FRACTION') {
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      maximumFractionDigits: 1,
      signDisplay: signed ? 'exceptZero' : 'auto',
    }).format(fact.value)
  }
  const number = new Intl.NumberFormat(locale, {
    maximumFractionDigits: fact.unit === 'MINUTES' ? 1 : 0,
    signDisplay: signed ? 'exceptZero' : 'auto',
  }).format(fact.value)
  return fact.unit === 'MINUTES' ? t('overview.units.minutes', { value: number }) : number
}

/** A registry code as a string key: `CONCEPT-CARE-QUALITY` → `CARE_QUALITY` (keys take no hyphens). */
const codeKey = (code: string, prefix: string): string => code.replace(prefix, '').replace(/-/g, '_')

/**
 * What an answer could not measure, by name: one of the five metrics, a
 * concept no metric answers yet, or an analytics method. A code the screen
 * has no words for keeps its registry id.
 */
export const subjectName = (subject: string, t: Translate, te: Exists): string => {
  const key = subject.startsWith('CONCEPT-')
    ? `analyst.concepts.${codeKey(subject, 'CONCEPT-')}`
    : subject.startsWith('METHOD-')
      ? `analyst.methods.${codeKey(subject, 'METHOD-')}`
      : null
  if (key) return te(key) ? t(key) : subject
  return metricName(subject, t)
}

/**
 * "It needs the time each patient arrives and the triage steps.", or null
 * when nothing named is missing. What the care journey needs is worded once,
 * with the Overview's coverage story (DECISIONS.md #39).
 */
export const needsText = (missing: string[], t: Translate, te: Exists, locale: string): string | null => {
  const named = (code: string): string | null =>
    te(`analyst.sources.${code}`) ? t(`analyst.sources.${code}`) : te(`coverage.inputs.${code}.needs`) ? t(`coverage.inputs.${code}.needs`) : null
  const sources = missing.flatMap((code) => named(code) ?? [])
  return sources.length > 0 ? t('analyst.notMeasurable.needs', { sources: listOf(sources, locale) }) : null
}

/** A day of the ISO week (1 is Monday) in the reader's language. 1 January 2024 was a Monday. */
const weekday = (iso: number, locale: string): string =>
  new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, iso)))

/** An hour of the day as the hour it starts to the hour it ends: "08:00 – 09:00". */
const hourRange = (hour: number, locale: string): string => {
  const format = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })
  return format.formatRange(new Date(Date.UTC(2024, 0, 1, hour)), new Date(Date.UTC(2024, 0, 1, hour + 1)))
}

/**
 * One group of a breakdown, in words. Hours and weekdays are the workspace's
 * own (the engine counts them in its time zone). Null for a key the screen
 * cannot name, so the number is left out rather than shown under a code.
 */
export const groupLabel = (
  group: { breakdown: Breakdown; key: string },
  t: Translate,
  te: Exists,
  locale: string,
): string | null => {
  const number = Number(group.key)
  switch (group.breakdown) {
    case 'WEEKDAY':
      return Number.isInteger(number) && number >= 1 && number <= 7 ? weekday(number, locale) : null
    case 'HOUR_OF_DAY':
      return Number.isInteger(number) && number >= 0 && number <= 23 ? hourRange(number, locale) : null
    case 'VISIT_TYPE':
      return te(`analyst.groups.visitType.${group.key}`) ? t(`analyst.groups.visitType.${group.key}`) : null
    case 'PROFESSIONAL_ROLE':
      // The platform's roles are worded once, with platform utilization; only "no role on record" is the Analyst's own.
      if (te(`overview.utilization.roles.${group.key}`)) return t(`overview.utilization.roles.${group.key}`)
      return te(`analyst.groups.role.${group.key}`) ? t(`analyst.groups.role.${group.key}`) : null
  }
}

/** What a fact is: its metric, what was measured, over which days, and for which group. Null when its group has no name. */
export const factLabel = (
  fact: Fact,
  period: { first: string; last: string; baselineFirst: string; baselineLast: string } | null,
  t: Translate,
  te: Exists,
  locale: string,
): string | null => {
  const range =
    period && fact.window === 'BASELINE'
      ? formatDayRange(period.baselineFirst, period.baselineLast, locale)
      : period
        ? formatDayRange(period.first, period.last, locale)
        : ''
  const covariate = fact.covariate ? t(`analyst.numbers.covariates.${fact.covariate}`) : ''
  const label = t(`analyst.numbers.measure.${fact.measure}`, { metric: metricName(fact.metricId, t), range, covariate })
  if (!fact.group) return label
  const group = groupLabel(fact.group, t, te, locale)
  return group === null ? null : t('analyst.numbers.grouped', { label, group })
}

/** The Overview's limits it can say without their numbers or day; those that need them have their own words here. */
const OVERVIEW_LIMITS = new Set(['NOT_CONNECTED', 'UNREACHABLE', 'NOT_LOADED'])
const OWN_LIMITS = new Set(['TOO_FEW', 'WAITING_ROOM_VISITS_ONLY', 'NOT_RECORDED_BEFORE', 'NOT_RECORDED'])
/** A care-journey measure's own (DECISIONS.md #39). The inputs it lacks are said under the answer; how many a share is of, in its figure. */
const CARE_LIMITS = new Set(['RECORDS_NOT_CONNECTED', 'LAB_ORDERS_NOT_RECORDED'])
// STALE_DATA says what the STALE readiness line already does.

/** A care measure looks back further than its period: one whose look-back reaches before the records says so its own way. */
const limitText = (metricId: string, code: string, t: Translate): string | null => {
  if (metricId in CONTINUITY_KEYS) {
    if (CARE_LIMITS.has(code)) return t(`overview.continuity.notes.${code}`)
    if (code === 'NOT_RECORDED_BEFORE') return t('analyst.limits.LOOKBACK_NOT_RECORDED')
  }
  if (OVERVIEW_LIMITS.has(code)) return t(`overview.notes.${code}`)
  return OWN_LIMITS.has(code) ? t(`analyst.limits.${code}`) : null
}


/**
 * What limits a metric an answer used, in words: its readiness, then each
 * limit it carries (S1 Step 7 check 3). Null for a READY metric with none.
 * A code the screen has no words for is left out rather than shown raw.
 */
export const readingText = (
  reading: { metricId: string; readiness: string; limitations: string[] },
  t: Translate,
): string | null => {
  const limits = reading.limitations.flatMap((code) => limitText(reading.metricId, code, t) ?? [])
  if (reading.readiness === 'READY' && limits.length === 0) return null
  const state = reading.readiness === 'READY' ? [] : [t(`analyst.readiness.${reading.readiness}`, { metric: metricName(reading.metricId, t) })]
  return [...state, ...limits].join(' ')
}
